import React, { ElementType, useCallback, useMemo } from "react";
import {
	ElementComponentData,
	getElement,
} from "../../data/form/elementSelectors";
import { componentMap } from "../../constants";
import { BaseElementProps } from "elements/types";
import { useSelector, useDispatch } from "react-redux";
import { State } from "data";
import { createModifiedDataPath } from "../../lib/forms/common";
import { ElementData } from "records/Element";
import { PatchConfig } from "data/form/types";
import { jsonPatch } from "data/form/formActions";

type ActionProps = {
	fid: string;
	id: string;
	index?: number;
	indexes?: number[];
	vid: string;
	uuid: string;
	rowIndex: number;
};

type DisplayProps = {
	rowIndex: number;
	index?: number;
	uuid: string;
};

export function useDisplayElement({ rowIndex, index = 0, uuid }: DisplayProps) {
	const eid = useMemo(
		() => `${rowIndex}/${uuid}/${index}`,
		[index, rowIndex, uuid]
	);
	return { eid };
}

export function useStandardElement({
	fid,
	id,
	vid,
	index,
	indexes,
	uuid,
	rowIndex,
}: ActionProps) {
	// Redux dispatcher
	const dispatch = useDispatch();

	const eid = useMemo(
		() => createModifiedDataPath(vid, rowIndex, id, index),
		[vid, rowIndex, id, index]
	);

	// The index attribute is used in instances where items of a
	// list can be edited separately, for example, a legacyArrayView

	const dataId = useMemo(
		() => (index !== undefined ? `${id}/${index}` : id),
		[id, index]
	);

	/**
	 * Set data and mark the element as ready to push its update
	 */
	const _setDataAndQueue = useCallback(
		(currentData: ElementComponentData, nextData: ElementData) => {
			const patch: PatchConfig[] = [];
			const op = currentData?.value === undefined ? "add" : "replace";
			if (indexes) {
				indexes?.forEach((index: number) => {
					const dataId = index !== undefined ? `${id}/${index}` : id;
					patch.push({
						path: dataId,
						op,
						id: dataId,
						uuid,
						rowIndex,
						vid,
						data: nextData,
					});
				});
			} else {
				patch.push({
					path: dataId,
					op,
					id: dataId,
					uuid,
					rowIndex,
					vid,
					data: nextData,
				});
			}
			dispatch(jsonPatch(fid, patch));
		},
		[indexes, dispatch, fid, id, uuid, rowIndex, vid, dataId]
	);

	const remove = useCallback(() => {
		const patch: PatchConfig[] = [
			{
				path: dataId,
				id: dataId,
				op: "remove",
				uuid,
				rowIndex,
				vid,
			},
		];
		dispatch(jsonPatch(fid, patch));
	}, [dataId, dispatch, fid, rowIndex, uuid, vid]);

	return {
		eid,
		dataId,
		setDataAndQueue: _setDataAndQueue,
		remove,
	};
}

function Element({ disabled, removeable = true, ...props }: BaseElementProps) {
	// props that can be overridden
	const parentOverrides = useMemo(
		() => ({
			...(disabled && { disabled }),
			removeable,
		}),
		[disabled, removeable]
	);

	const elementProps = useSelector((state: State) =>
		getElement(state, props)
	);

	// Get the Element component
	const ElementComponent: ElementType = componentMap[elementProps.elem];

	// If we can't map the elem type to a component we return null
	if (!ElementComponent) {
		console.warn(
			`Couldn't find a matching element for elem type of ${elementProps.elem}`
		);
		return null;
	}

	return (
		<ElementComponent {...props} {...elementProps} {...parentOverrides} />
	);
}

export default Element;

// function Element({
//     fid,
//     uuid,
//     index,
//     rowIndex = 0,
//     displayOnly = false,
// }: BaseElementProps) {
//     // const formContext = useContext(FormContext);

//     // Redux dispatcher
//     // const dispatch = useDispatch();

//     // Create a new instance of our element props selector
//     // const getElement = useMemo(createGetElementSelector, []);

//     // Memoize a selector props object to minimise false renders
//     const selectorProps = useMemo(() => ({ fid, uuid }), [
//         fid,
//         uuid
//     ]);

//     const eid = useMemo(() => createModifiedDataPath(uuid, rowIndex), [uuid, rowIndex]);

//     // Grab the element props
//     const properties = useSelector((state: State) => getElement(state, selectorProps));

//     if (isViewElement(properties.elem)) {
//         return <ViewElement />;
//     }

//     return <StandardElement />;

//     // if (isViewElement(properties.elem)) {
//     //     return <ViewElement {...properties} />;
//     // } else {
//     //     return <StandardElement {...properties} />;
//     // }

//     /**
//      * The element id is used for display purposes - it is a uniquely formated id that we can reproduce
//      * to reference other elements if needs be, it is required where elements are linked in the form definition
//      * using id's - as is the case with "describedBy". UUID's aren't useful here because they are dynamically
//      * generated
//      */
//     // const eid = useMemo(() => (`${vid}/${index !== undefined ? index + "/" : ""}${element.id}`), [index, vid, element.id]);

//     /**
//      * The eid uniquely identifies elements and is used for saving data to state, we have to generate
//      * this because UUID's aren't useful in cases where we have multiple rows that use the same form
//      * definition and therefore the same UUID, an example of this is seen in the multiReferenceView.
//      * We create a unique ID that can be used to save data into our temporary redux store by
//      * using the vid, rowIndex and id
//      */
//     // const eid = useMemo(() => createModifiedDataPath(uuid, rowIndex), [uuid, rowIndex]);

//     /**
//      * If we had any kind of problems loading our props from
//      * redux, we just return null here. This could happen if for some
//      * reason our Form disappears from state.
//      */
//     if (!properties) {
//         console.warn(`Couldn't load props for ${uuid}.`);
//         return null;
//     }

// -----------
// Any computed attributes go here
// -----------

// Described by aria attribute
// TODO: will need to update this to work with the new pointer above
// const describedBy = useMemo(() => {
//     if (!element.describedBy) return undefined;
//     return (`${vid}/${index !== undefined ? index + "/" : ""}${element.describedBy}`)
// }, [element.describedBy, vid, index]);

// -----------
// Callbacks
// -----------

//     /**
//      * Set data
//      */
// const _setData: SetElementDataFunc = useCallback(
//     (nextData) => {
//         // BUG: maintain current value if it is set (same for setDataAndQueue)
//         if (eid)
//             dispatch(
//                 setData(fid, {
//                     [eid]: {
//                         vid,
//                         id,
//                         currentValue: data?.value,
//                         index,
//                         ...nextData,
//                     },
//                 })
//             );
//     },
//     [eid, dispatch, fid, vid, id, data, index]
// );

//     /**
//      * Set data and mark the element as ready to push its update
//      */
// const _setDataAndQueue: SetElementDataFunc = useCallback(
//     (nextData) => {
//         if (eid)
//             dispatch(
//                 setDataAndQueue(fid, {
//                     [eid]: {
//                         id,
//                         vid,
//                         currentValue: data?.value,
//                         index,
//                         ...nextData,
//                     },
//                 })
//             );
//     },
//     [eid, dispatch, fid, id, vid, data, index]
// );

//     /**
//      * Dispatch a updateQueue event so that the elements
//      * modified value can be pushed to the server when ready
//      */
//     // const pushModifiedValue = useCallback(() => {
//     //     if (eid && properties.id) dispatch(updateQueue({ fid, uuid: eid, id: properties.id, vid }));
//     // }, [dispatch, eid, properties.id, fid, vid]);

//     /**
//      * If we had any kind of problems loading our props from
//      * redux, we just return null here. This could happen if for some
//      * reason our Form disappears from state.
//      */
// if (!properties) {
//     console.warn(`Couldn't load props for ${uuid}.`);
//     return null;
// }

// /**
//  * DisplayOnly
//  */
// if (displayOnly)
//     return (
//         <DisplayOnlyValue
//             elem={properties.elem}
//             label={properties.label}
//             data={data || createElementDataRecord(id)}
//         />
//     );

// // Get the Element component
// const ElementComponent: ReactType = componentMap[properties.elem];

// // If we can't map the elem type to a component we return null
// if (!ElementComponent) {
//     console.warn(
//         `Couldn't find a matching element for elem type of ${properties.elem}`
//     );
//     return null;
// }

// const elemProps: ElementProps = {
//     ...properties,
//     disabled: formContext.disabled || properties.disabled,
//     data: data || createElementDataRecord(id),
//     eid,
//     fid,
//     uuid,
//     // describedBy,
//     rowIndex,
//     setData: _setData,
//     setDataAndQueue: _setDataAndQueue,
//     pushValue: pushModifiedValue,
// };

// return <ElementComponent {...elemProps} />;
// }
